import { useTranslation } from '@fluentsms/i18n';
import { IconButton, InputAdornment, makeStyles, TextField, TextFieldProps, Theme, Tooltip } from '@material-ui/core';
import { Close, Search } from '@material-ui/icons';
import React, { ChangeEvent, useState } from 'react';

type Props = TextFieldProps & {
  tooltip?: string;
  onQueryChange?(value: string): void;
};

const useStyles = makeStyles((theme: Theme) => ({
  searchField: {
    width: 200,
    marginLeft: theme.spacing(1),
    '& input': {
      paddingBottom: theme.spacing(0.75),
    },
  },
  iconColor: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
  },
}));

export function ServerSideDataGridSearch({
  onQueryChange = noop,
  placeholder,
  defaultValue,
  tooltip = '',
  ...props
}: Props): JSX.Element {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [searchText, setSearchText] = useState<string>('');

  // Sanitize input for SSRM - https://dev.azure.com/FirstAmCorp/FluentTPS/_workitems/edit/2337134
  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>): void => {
    const value: string = e.target.value;
    const regex = /[*()+[\]?]/g;
    const sanitizedvalue: string = value.replace(regex, '');

    if (!regex.test(value)) {
      setSearchText(sanitizedvalue);
      onQueryChange(sanitizedvalue);
    }
  };

  const onClickHandler = (): void => {
    setSearchText('');
    onQueryChange('');
  };

  return (
    <TextField
      {...props}
      className={classes.searchField}
      size="small"
      data-testid="data-grid-search-input"
      placeholder={placeholder || t('dataGridSearch.searchPlaceholder')}
      defaultValue={defaultValue}
      value={searchText}
      onChange={onChangeHandler}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" data-testid="search-icon">
            {tooltip ? (
              <Tooltip
                title={tooltip}
                hidden={true}
                placement="top"
                arrow={true}
                data-testid="data-grid-searchIcon-tooltip"
              >
                <Search className={classes.iconColor} />
              </Tooltip>
            ) : (
              <Search className={classes.iconColor} />
            )}
          </InputAdornment>
        ),
        endAdornment: searchText && (
          <InputAdornment position="end">
            <IconButton size="small" data-testid="clear-button" onClick={onClickHandler}>
              <Close fontSize="small" className={classes.iconColor} />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

function noop() {
  // Pass
}
